/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import * as teamsApi from "../../../../api/teams";
import * as orgAdmin from "../../../../api/orgAdmin";

const TEXT_BUTTON_SX = { color: "text.color", textTransform: "none" };
const ACCENT_BUTTON_SX = { backgroundColor: "accent.solid", color: "#fff", textTransform: "none" };

/**
 * The two ways to reach team membership differ only in which endpoints they
 * call and what authorises them — self-service requires you to be a team admin,
 * the org-admin console does not require membership at all. Everything else
 * (the list, add-by-email, role select, remove) was duplicated character for
 * character, so the difference is expressed as data rather than a second file.
 */
const TEAM_MEMBER_APIS = {
  /** Self-service: /drive/teams/{id}/members — caller must be a team admin. */
  team: (teamId) => ({
    list: () => teamsApi.listMembers(teamId),
    lookup: (email) => teamsApi.lookupMember(teamId, email),
    add: (userId) => teamsApi.addMember(teamId, { userId }),
    remove: (userId) => teamsApi.removeMember(teamId, userId),
    setRole: (userId, role) => teamsApi.setMemberRole(teamId, userId, role),
  }),
  /** Admin console: /drive/admin/teams/{id}/members — org admin, any team. */
  orgAdmin: (teamId) => ({
    list: () => orgAdmin.listTeamMembers(teamId),
    lookup: (email) => orgAdmin.lookupTeamMember(teamId, email),
    add: (userId) => orgAdmin.addTeamMember(teamId, userId),
    remove: (userId) => orgAdmin.removeTeamMember(teamId, userId),
    setRole: (userId, role) => orgAdmin.setTeamMemberRole(teamId, userId, role),
  }),
};

/**
 * Team membership. `canManage` hides the add/remove/role controls for a plain
 * member; it is not the security boundary — every route re-checks server-side.
 */
const TeamMembersDialog = ({ open, title = "Team members", variant, teamId, canManage = true, onClose }) => {
  // Built here, from primitives, and memoised. Taking a ready-made `api` object
  // as a prop looked tidier but rebuilt it every render, which changed `load`
  // every render and re-fired the effect below in a loop.
  const api = useMemo(() => TEAM_MEMBER_APIS[variant](teamId), [variant, teamId]);

  const [members, setMembers] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setMembers(await api.list());
    } catch (err) {
      setError(err.message || "Could not load members.");
      setMembers([]);
    }
  }, [api]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setEmail("");
    setMembers(null);
    load();
  }, [open, load]);

  /** Every mutation shares the same busy/error/reload envelope. */
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      // The API refuses to remove or demote the last admin — surface it verbatim.
      setError(err.message || "Could not update the team.");
    } finally {
      setBusy(false);
    }
  };

  const add = () =>
    run(async () => {
      const user = await api.lookup(email.trim());
      await api.add(user.user_id);
      setEmail("");
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {canManage && (
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Add by email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
            <Button variant="contained" sx={ACCENT_BUTTON_SX} disabled={busy || !email.trim()} onClick={add}>
              Add
            </Button>
          </Box>
        )}

        {members === null ? (
          <Typography sx={{ fontSize: 13.5 }}>Loading…</Typography>
        ) : members.length === 0 ? (
          <Typography sx={{ fontSize: 13.5 }}>No members yet.</Typography>
        ) : (
          <List dense disablePadding>
            {members.map((m) => (
              <ListItem
                key={m.id}
                disableGutters
                secondaryAction={
                  canManage && (
                    <Tooltip title="Remove from team">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy}
                          onClick={() => run(() => api.remove(m.user_id))}
                        >
                          <PersonRemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
              >
                <ListItemText
                  primary={m.user_full_name || m.user_email || m.user_id}
                  secondary={m.user_full_name ? m.user_email : null}
                  primaryTypographyProps={{ fontSize: 13.5 }}
                  secondaryTypographyProps={{ fontSize: 11.5 }}
                />
                {canManage ? (
                  <Select
                    size="small"
                    value={m.role}
                    disabled={busy}
                    onChange={(e) => run(() => api.setRole(m.user_id, e.target.value))}
                    sx={{ fontSize: 11.5, mr: 1, minWidth: 96 }}
                  >
                    <MenuItem value="member" sx={{ fontSize: 12.5 }}>Member</MenuItem>
                    <MenuItem value="admin" sx={{ fontSize: 12.5 }}>Admin</MenuItem>
                  </Select>
                ) : (
                  <Chip size="small" label={m.role} sx={{ fontSize: 10.5, mr: 1, textTransform: "capitalize" }} />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={TEXT_BUTTON_SX}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamMembersDialog;
